import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import Typography from "@mui/material/Typography";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import AIPopup from "./aiPopup.js";
import { RadioButton } from "primereact/radiobutton";
import { Button } from "react-bootstrap";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Swal from "sweetalert2";
import { isUnusableAiAnswer, stripMarkdown, loadAnswersFormatted, saveAnswersFormatted, isTableAnswer, buildS1_6TableHtmlFromAnswers, loadS1_6ColumnHeaders } from "./utils/csrdAnswerUtils";

const RightContainer = forwardRef(function RightContainer({ selectedContent, questions = [], tabData }, ref) {
  const companyid = localStorage.getItem("companyid");
  const username = localStorage.getItem("userName");
  const [answers, setAnswers] = useState({});
  const [answers1, setAnswers1] = useState({});
  // const [stepData4, setStepData4] = useState({});
  const [savedStatus, setSavedStatus] = useState({});
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [stepData, setStepData] = useState([]);
  const [expandedTabs, setExpandedTabs] = useState({}); // State to track expanded tabs
  const [selectedOptions, setSelectedOptions] = useState({});
  const [blobAnswers, setBlobAnswers] = useState([]);
  const [loading, setLoading] = React.useState(false);
  const selectedSubSubItem = tabData
    .flatMap((item) => item.subItems)
    .flatMap((subItem) => subItem.subSubItems)
    .find((subSubItem) => subSubItem.label === selectedContent);

  const isExpanded = expandedTabs[selectedContent];

  const toggleExpand = () => {
    setExpandedTabs((prevExpandedTabs) => ({
      ...prevExpandedTabs,
      [selectedContent]: !prevExpandedTabs[selectedContent],
    }));
  };

  const CustomWidthTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 500,
    },
  });

  const NoMaxWidthTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: "none",
    },
  });

  const openModal = (question, answer) => {
    setSelectedQuestion(question);
    setSelectedAnswer(answer);
    setAiModalOpen(true);
  };

  const closeModal = () => {
    setAiModalOpen(false);
  };

  /// Reset selected option when selectedSubSubItem changes
  useEffect(() => {
    setSelectedOptions({});
    // Retrieve selected MDR option from localStorage
    const savedOptions = localStorage.getItem("selectedOptions");
    if (savedOptions) {
      setSelectedOptions(JSON.parse(savedOptions));
    }
  }, [selectedSubSubItem]);

  const handleOptionChange = (item, value) => {
    const updatedOptions = {
      ...selectedOptions,
      [item]: value,
    };
    setSelectedOptions(updatedOptions);
    // Save selected MDR option to localStorage
    localStorage.setItem("selectedOptions", JSON.stringify(updatedOptions));
  };

  // Function to update the answer for a question
  const handleAnswerChange = (question, answer, id) => {
    setAnswers1((prevAnswers) => {
      const updatedAnswers = {
        ...prevAnswers,
        [id]: answer,
      };
      const storedAnswers = JSON.parse(localStorage.getItem("answers") || "{}");
      if (storedAnswers[id]) {
        delete storedAnswers[id];
      }
      const formatted = loadAnswersFormatted();
      if (formatted[id]) {
        delete formatted[id];
        saveAnswersFormatted(formatted);
      }
      setAnswers((prevAnswers) => {
        const updatedAnswers = { ...prevAnswers };
        delete updatedAnswers[id];
        return updatedAnswers;
      });
      localStorage.setItem("answers", JSON.stringify(storedAnswers));
      localStorage.setItem("answers1", JSON.stringify(updatedAnswers));
      return updatedAnswers;
    });
  };

  // Helper to insert or replace a step4 entry so the latest saved table or
  // answer is always used for the PDF (no stale first-saved versions).
  const upsertStepData = (newData) => {
    setStepData((prevData) => {
      if (!prevData || prevData.length === 0) {
        const arr = [newData];
        localStorage.setItem("step4", JSON.stringify(arr));
        return arr;
      }

      const idx = newData.id ? prevData.findIndex((item) => item.id === newData.id) : prevData.findIndex((item) => item.question === newData.question && item.module === newData.module);

      const updated = idx >= 0 ? [...prevData.slice(0, idx), newData, ...prevData.slice(idx + 1)] : [...prevData, newData];

      localStorage.setItem("step4", JSON.stringify(updated));
      return updated;
    });
  };

  useEffect(() => {
    setSelectedOptions({});
    const savedAnswers = localStorage.getItem("answers");
    if (savedAnswers) {
      setAnswers(JSON.parse(savedAnswers));
    }
    const savedAnswers1 = localStorage.getItem("answers1");
    const answersFormatted = loadAnswersFormatted();
    if (savedAnswers1) {
      const parsed = JSON.parse(savedAnswers1);
      const cleaned = Object.fromEntries(
        Object.entries(parsed).filter(([, value]) => !isUnusableAiAnswer(value))
      );
      Object.entries(answersFormatted).forEach(([key, value]) => {
        if (!cleaned[key] && !isUnusableAiAnswer(value)) {
          cleaned[key] = stripMarkdown(value);
        }
      });
      setAnswers1(cleaned);
      const needsSync =
        Object.keys(cleaned).length !== Object.keys(parsed).length ||
        Object.entries(parsed).some(([key, value]) => cleaned[key] !== value);
      if (needsSync) {
        localStorage.setItem("answers1", JSON.stringify(cleaned));
      }
    }

    // Retrieve saved stepData from localStorage
    const savedStepData = localStorage.getItem("step4");
    if (savedStepData) {
      let stepData = JSON.parse(savedStepData);
      // Create a Map to store unique questions, keeping the last occurrence
      const uniqueStepData = new Map();
      for (let i = stepData.length - 1; i >= 0; i--) {
        uniqueStepData.set(stepData[i].question, stepData[i]);
      }
      // Convert Map values back to an array
      stepData = Array.from(uniqueStepData.values());
      localStorage.setItem("step4", JSON.stringify(stepData));
      setStepData(stepData);
      const savedAnswers = JSON.parse(localStorage.getItem("answers") || "{}");
      const savedStatus = stepData.reduce((acc, item) => {
        const questionKey = item.question?.trim();
        if (!questionKey) return acc;
        if (item.id && savedAnswers[item.id]) {
          acc[questionKey] = true;
        } else if (isTableAnswer(item.answer)) {
          acc[questionKey] = true;
        }
        return acc;
      }, {});
      localStorage.setItem("savedStatus", JSON.stringify(savedStatus));
      setSavedStatus(savedStatus);
    }
  }, []);

  const GenerateAIAnswers = async (questions) => {
    setLoading(true);

    const allQuestions = questions.map((question) => ({
      question: question.question,
    }));
    const questionsArray = allQuestions.map((q) => q.question);
    const selectedQuestions = questionsArray;
    const report_name = localStorage.getItem("report_name");
    try {
      let res = await fetch(`${process.env.REACT_APP_BASE_API_URL}GenerateAnswersWithAI`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          company_id: companyid,
          report_name: report_name,
          report_type: "csrd",
          questions: selectedQuestions,
        }),
      });
      if (res.status === 200) {
        const responseJson = await res.json();
        const updatedAnswers = {};
        const storedAnswers = JSON.parse(localStorage.getItem("answers") || "{}");
        const storedAnswers1 = JSON.parse(localStorage.getItem("answers1") || "{}");
        const formattedAnswers = loadAnswersFormatted();
        if (responseJson && responseJson.results && Array.isArray(responseJson.results)) {
          responseJson.results.forEach((item) => {
            const matchingQuestion = questions.find((q) => q.question === item.question);
            const matchingQuestion1 = questions.find((q) => `q-${q.id}` === item);
            //if (matchingQuestion && item.answer && item.answer !== "No Answer.") {
            if (matchingQuestion && item.answer && item.AIGenerated === true) {
              if (isUnusableAiAnswer(item.answer)) {
                return;
              }
              formattedAnswers[matchingQuestion.id] = item.answer;
              updatedAnswers[matchingQuestion.id] = stripMarkdown(item.answer);
              const key = `${matchingQuestion.id}`;
              if (storedAnswers[key]) {
                delete storedAnswers[key];
                delete storedAnswers1[key];
              }
              const questionKey = `${item.question}`;
              setSavedStatus((prevSavedStatus) => {
                const newStatus = { ...prevSavedStatus };
                delete newStatus[questionKey];
                return newStatus;
              });
              setAnswers((prevAnswers) => {
                const updatedAnswers = { ...prevAnswers };
                delete updatedAnswers[matchingQuestion.id];
                return updatedAnswers;
              });
            }
          });
          saveAnswersFormatted(formattedAnswers);
          localStorage.setItem("answers", JSON.stringify(storedAnswers));
          localStorage.setItem("answers1", JSON.stringify(storedAnswers1));
          setAnswers1((prevAnswers) => ({
            ...prevAnswers,
            ...updatedAnswers,
          }));

          const isAnswerSaved = (question) => !!savedStatus[question.trim()];
          Swal.fire({
            width: 400,
            heightAuto: false,
            icon: "success",
            html: `<h4 style="color:#407775;font-size: 20px;">Success!</h4><p style="font-size:16px;line-height: 20px;">AI Generated Answers ${responseJson.ai_generated_count}</p>`,
            iconHtml: '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />',
          });
        } else {
          console.error("Invalid response format or empty results array");
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Error reading blob:", error);
      setLoading(false);
    }
  };

  const fetchData = async (title) => {
    const savedAnswers = localStorage.getItem("answers");
    if (savedAnswers) {
      const parsedAnswers = JSON.parse(savedAnswers);
      const hasAnswers = questions.some((question) => parsedAnswers[question.id]);
      if (hasAnswers) {
        Swal.fire({
          icon: "warning",
          title: "Generate with AI",
          text: "Answers are already saved. Generating with AI will replace your answers. Are you sure you want to proceed?",
          showCancelButton: true,
          confirmButtonText: "Yes",
          cancelButtonText: "No",
          customClass: {
            confirmButton: "swal-btn",
            cancelButton: "swal-btn",
          },
        }).then((result) => {
          if (result.isConfirmed) {
            GenerateAIAnswers(questions);
          } else {
            Swal.fire({
              title: "Cancelled",
              text: "Your existing answers are retained.",
              icon: "error",
              confirmButtonText: "OK",
              customClass: {
                icon: "swal-cancelled-icon",
                confirmButton: "swal-btn",
              },
            });
            //GenerateAIAnswers(questions);
          }
        });
      } else {
        GenerateAIAnswers(questions);
      }
    } else {
      GenerateAIAnswers(questions);
    }
  };

  const markQuestionSaved = (question) => {
    const key = question.trim();
    setSavedStatus((prevSavedStatus) => {
      const next = { ...prevSavedStatus, [key]: true };
      localStorage.setItem("savedStatus", JSON.stringify(next));
      return next;
    });
  };

  const handleTableSave = (title, question, answer, module, fun, id) => {
    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    const toArray = (x) => (Array.isArray(x) ? x : x ? [x] : []);

    const findFirstType = (node, type) => {
      if (!node || typeof node !== "object") return null;
      if (node.type === type) return node;
      const kids = toArray(node.props?.children);
      for (const k of kids) {
        const found = findFirstType(k, type);
        if (found) return found;
      }
      return null;
    };

    const findAllTypes = (node, type, acc = []) => {
      if (!node || typeof node !== "object") return acc;
      if (node.type === type) acc.push(node);
      const kids = toArray(node.props?.children);
      kids.forEach((k) => findAllTypes(k, type, acc));
      return acc;
    };

    const getNodeValueOrText = (node) => {
      if (node == null) return "";
      if (typeof node === "string" || typeof node === "number") return String(node);
      if (typeof node !== "object") return "";
      const props = node.props || {};
      // Prefer controlled inputs/textarea/select value
      if (props.value !== undefined && props.value !== null) return String(props.value);
      // Some components nest the value under children.props.value
      const kids = toArray(props.children);
      for (const k of kids) {
        const v = getNodeValueOrText(k);
        if (v !== "") return v;
      }
      // Fallback: plain children string
      if (props.children != null && (typeof props.children === "string" || typeof props.children === "number")) {
        return String(props.children);
      }
      return "";
    };

    const serializeReactTableToHtml = (root) => {
      const tableEl = findFirstType(root, "table");
      if (!tableEl) return "";
      const tableChildren = toArray(tableEl.props?.children);
      const theadEl = tableChildren.find((c) => c?.type === "thead");
      const tbodyEl = tableChildren.find((c) => c?.type === "tbody");

      const theadTrs = theadEl ? findAllTypes(theadEl, "tr") : [];
      const tbodyTrs = tbodyEl ? findAllTypes(tbodyEl, "tr") : [];

      // If no tbody, still try to find tr under table
      const bodyRows = tbodyTrs.length ? tbodyTrs : findAllTypes(tableEl, "tr").slice(theadTrs.length);

      // Build thead: support multi-row headers
      let theadHtml = "";
      if (theadTrs.length) {
        theadHtml = theadTrs
          .map((tr) => {
            const cells = toArray(tr.props?.children).filter((c) => c && (c.type === "th" || c.type === "td"));
            if (cells.length === 0) return "";
            const rowHtml = cells
              .map((cell) => {
                const tag = cell.type === "td" ? "td" : "th";
                const colSpan = cell.props?.colSpan != null ? ` colspan="${cell.props.colSpan}"` : "";
                const rowSpan = cell.props?.rowSpan != null ? ` rowspan="${cell.props.rowSpan}"` : "";
                return `<${tag}${rowSpan}${colSpan}>${escapeHtml(getNodeValueOrText(cell.props?.children))}</${tag}>`;
              })
              .join("");
            return `<tr>${rowHtml}</tr>`;
          })
          .filter(Boolean)
          .join("");
      }

      // If header missing, derive column count from first body row
      if (!theadHtml) {
        const firstBody = bodyRows[0];
        const cells = toArray(firstBody?.props?.children).filter((c) => c && (c.type === "td" || c.type === "th"));
        const n = Math.max(1, cells.length);
        theadHtml = `<tr>${new Array(n).fill(0).map(() => "<th></th>").join("")}</tr>`;
      }

      const tbodyHtml = bodyRows
        .map((tr) => {
          const cells = toArray(tr.props?.children).filter((c) => c && (c.type === "td" || c.type === "th"));
          if (cells.length === 0) return "";
          const rowHtml = cells
            .map((cell) => {
              const colSpan = cell.props?.colSpan != null ? ` colspan="${cell.props.colSpan}"` : "";
              const rowSpan = cell.props?.rowSpan != null ? ` rowspan="${cell.props.rowSpan}"` : "";
              // Extract inner value/text (textarea/input/select or plain text)
              const val = getNodeValueOrText(cell.props?.children);
              return `<td${rowSpan}${colSpan}>${escapeHtml(val)}</td>`;
            })
            .join("");
          return `<tr>${rowHtml}</tr>`;
        })
        .filter(Boolean)
        .join("");

      return `<table><thead>${theadHtml}</thead><tbody>${tbodyHtml}</tbody></table>`;
    };

    // Generic table save path: works for most CSRD tables (including GOV-4).
    // Skip Scope123 and S1-6 (6Column1) — they need dedicated serializers.
    if (answer && typeof answer === "object" && fun !== "Scope123" && fun !== "6Column1") {
      const generic = serializeReactTableToHtml(answer);
      if (generic && /<tbody>[\s\S]*<\/tbody>/i.test(generic)) {
        markQuestionSaved(question);
        upsertStepData({
          title,
          question,
          answer: generic,
          module,
          id,
        });
        return;
      }
    }

    //4 Column
    if ("4Column" === fun || "5Column" === fun || "3Column" === fun) {
      if (answer && answer.props) {
        // Expected structure for GOV-4:
        // <div> <table> <thead><tr><th>...</th></tr></thead> <tbody><tr><td><textarea value=... /></td>...</tr></tbody> </table> </div>
        const tableEl = findFirstType(answer, "table");
        const tableChildren = toArray(tableEl?.props?.children);
        const theadEl = tableChildren.find((c) => c?.type === "thead") || tableChildren[0];
        const tbodyEl = tableChildren.find((c) => c?.type === "tbody") || tableChildren[1];

        // Be resilient to whitespace/text nodes: locate first <tr> and all <th> under thead
        const headerTr = findFirstType(theadEl, "tr");
        let thElements = headerTr ? findAllTypes(headerTr, "th") : findAllTypes(theadEl, "th");
        let tbodyRows = toArray(tbodyEl?.props?.children).filter((r) => r && r.type === "tr");
        if (tbodyRows.length === 0) {
          tbodyRows = findAllTypes(tbodyEl, "tr");
        }

        let tableArray = "<table>";
        tableArray += "<thead><tr>";
        // If we still didn't find headers, derive a blank header based on first body row cell count
        if (!thElements || thElements.length === 0) {
          const firstRowCells = toArray(tbodyRows[0]?.props?.children).filter((c) => c && (c.type === "td" || c.type === "th"));
          const n = Math.max(1, firstRowCells.length);
          thElements = new Array(n).fill(null).map((_, i) => ({ props: { children: i === 0 ? "" : "" } }));
        }

        thElements.forEach((thEl) => {
          // th can have string children
          const headerText = thEl?.props?.children;
          tableArray += "<th>" + escapeHtml(headerText) + "</th>";
        });
        tableArray += "</tr></thead>";
        tableArray += "<tbody>";

        tbodyRows.forEach((rowEl) => {
          if (!rowEl) return;
          const cells = toArray(rowEl?.props?.children).filter((c) => c && (c.type === "td" || c.type === "th"));
          tableArray += "<tr>";
          cells.forEach((cellEl) => {
            if (!cellEl) return;
            // GOV-4 uses: <td><textarea value=... /></td>
            const inner = cellEl?.props?.children;
            const innerArray = toArray(inner);
            const firstInner = innerArray[0] || inner;
            const innerProps = firstInner?.props || {};
            const value = innerProps?.value !== undefined ? innerProps.value : innerProps?.children;
            tableArray += "<td>" + escapeHtml(value) + "</td>";
          });
          tableArray += "</tr>";
        });

        tableArray += "</tbody></table>";

        markQuestionSaved(question);

        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
    if ("4ColumnD" === fun) {
      if (answer && answer.props && answer.props.children) {
        const tableEl = findFirstType(answer, "table");
        const tableChildren = toArray(tableEl?.props?.children);
        const theadEl = tableChildren.find((c) => c?.type === "thead") || tableChildren[0];
        const tbodyEl = tableChildren.find((c) => c?.type === "tbody") || tableChildren[1];

        const headerTr = findFirstType(theadEl, "tr");
        let thElements = headerTr ? findAllTypes(headerTr, "th") : findAllTypes(theadEl, "th");
        let tbodyRows = toArray(tbodyEl?.props?.children).filter((r) => r && r.type === "tr");
        if (tbodyRows.length === 0) {
          tbodyRows = findAllTypes(tbodyEl, "tr");
        }

        let tableArray = "<table><thead><tr>";
        if (!thElements || thElements.length === 0) {
          const firstRowCells = toArray(tbodyRows[0]?.props?.children).filter((c) => c && (c.type === "td" || c.type === "th"));
          const n = Math.max(1, firstRowCells.length);
          thElements = new Array(n).fill(null).map(() => ({ props: { children: "" } }));
        }

        thElements.forEach((thEl) => {
          const headerText = thEl?.props?.children;
          tableArray += "<th>" + escapeHtml(headerText) + "</th>";
        });
        tableArray += "</tr></thead><tbody>";

        tbodyRows.forEach((rowEl) => {
          if (!rowEl) return;
          const cells = toArray(rowEl?.props?.children).filter((c) => c && (c.type === "td" || c.type === "th"));
          tableArray += "<tr>";
          cells.forEach((cellEl) => {
            if (!cellEl) return;
            const inner = cellEl?.props?.children;
            const innerArray = toArray(inner);
            const firstInner = innerArray[0] || inner;
            const innerProps = firstInner?.props || {};
            const value = innerProps?.value !== undefined ? innerProps.value : innerProps?.children;
            tableArray += "<td>" + escapeHtml(value) + "</td>";
          });
          tableArray += "</tr>";
        });

        tableArray += "</tbody></table>";

        markQuestionSaved(question);

        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
    if ("2ColumnD" === fun) {
      if (answer && answer.props && answer.props.children) {
        const headerArray = answer.props.children[0]?.props?.children?.props?.children;
        const rawChildrenArray = answer.props.children[1]?.props?.children;
        const childrenArray = Array.isArray(rawChildrenArray) ? rawChildrenArray : rawChildrenArray ? [rawChildrenArray] : [];
        let tableArray = "<table>";

        tableArray += "<thead><tr>";
        tableArray += '<th colspan="' + headerArray.props.colSpan + '">' + headerArray.props.children + "</th>";
        tableArray += "</tr></thead>";

        tableArray += "<tbody>";
        childrenArray.forEach((child) => {
          if (!child) return;
          tableArray += "<tr>";
          const cells = Array.isArray(child.props?.children) ? child.props.children : child.props?.children ? [child.props.children] : [];
          cells.forEach((child1) => {
            if (!child1) return;
            const ifArray = child1.props?.children?.props;
            const elseArray = child1.props?.children;

            if (ifArray !== undefined) {
              tableArray += "<td>" + (ifArray.value != null ? ifArray.value : "") + "</td>";
            } else {
              tableArray += "<th>" + (elseArray != null ? elseArray : "") + "</th>";
            }
          });
          tableArray += "</tr>";
        });

        tableArray += "</tbody>";
        tableArray += "</table>";
        markQuestionSaved(question);
        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
    if ("Scope123" === fun) {
      const thead11 = '<tr><th rowSpan="2">E1-6 – Gross Scopes 1, 2, 3 and Total GHG emissions</th><th colSpan="6">Retrospective</th><th colSpan="4">Milestones and target years</th></tr>' + "<tr><th>Base year</th><th>Comparative (N-3)</th><th>Comparative (N-2)</th><th>Comparative (N-1)</th><th>N-Current Year</th><th>% N / N-1</th><th>2025</th><th>2030</th><th>2050</th><th>Annual % target / Base year</th></tr>";

      const buildFallbackTable = () => {
        let t = "<table><thead>" + thead11 + "</thead><tbody>";
        try {
          const ta = JSON.parse(localStorage.getItem("tableAnswers") || "{}");
          const keys = Object.keys(ta).filter((k) => k.startsWith("e1_6_1-"));
          if (keys.length > 0) {
            t += '<tr><td colspan="11">E1-6 data (from saved answers)</td></tr>';
            keys.slice(0, 8).forEach((k) => {
              t += "<tr><td>" + (k.replace("e1_6_1-", "") || "").replace(/</g, "&lt;") + '</td><td colspan="10">' + (ta[k] != null ? String(ta[k]).replace(/</g, "&lt;") : "") + "</td></tr>";
            });
          } else {
            t += '<tr><td colspan="11">No E1-6 data saved yet.</td></tr>';
          }
        } catch (_) {
          t += '<tr><td colspan="11">Unable to load table data.</td></tr>';
        }
        return t + "</tbody></table>";
      };

      if (answer && answer.props && answer.props.children) {
        // Unwrap: E1-6 table is wrapped in div -> table (wrapper class can have multiple tokens)
        const wrapperClass = String(answer.props?.className || "");
        const isDivWrapper = answer.type === "div" && wrapperClass.includes("table-scroll-wrapper-custom");
        const tableEl = isDivWrapper ? findFirstType(answer, "table") : answer.type === "table" ? answer : findFirstType(answer, "table");
        if (!tableEl?.props?.children) {
          const tableArray = buildFallbackTable();
          markQuestionSaved(question);
          setStepData((prevData) => {
            const newData = { title, question, answer: tableArray, module, id };
            if (!prevData) {
              localStorage.setItem("step4", JSON.stringify([newData]));
              return [newData];
            }
            const idx = id ? prevData.findIndex((item) => item.id === id) : -1;
            const updated = idx >= 0 ? [...prevData.slice(0, idx), newData, ...prevData.slice(idx + 1)] : [...prevData, newData];
            localStorage.setItem("step4", JSON.stringify(updated));
            return updated;
          });
          return;
        }
        // Extract tbody rows robustly
        const tableChildren = Array.isArray(tableEl.props.children) ? tableEl.props.children : [tableEl.props.children];
        const tbodyEl = tableChildren.find((c) => c?.type === "tbody") || findFirstType(tableEl, "tbody");
        const childrenArray = tbodyEl?.props?.children;
        const rowsRaw = Array.isArray(childrenArray) ? childrenArray : childrenArray ? [childrenArray] : [];
        const rows = rowsRaw.filter((r) => r && r.type === "tr").length ? rowsRaw.filter((r) => r && r.type === "tr") : findAllTypes(tbodyEl, "tr");

        let tableArray = "<table>";
        tableArray += "<thead>" + thead11 + "</thead>";
        tableArray += "<tbody>";

        if (rows.length === 0) {
          // Fallback: build minimal table from localStorage so PDF has content when React tree parsing fails
          try {
            const ta = JSON.parse(localStorage.getItem("tableAnswers") || "{}");
            const keys = Object.keys(ta).filter((k) => k.startsWith("e1_6_1-"));
            if (keys.length > 0) {
              tableArray += '<tr><td colspan="11">E1-6 data (from saved answers)</td></tr>';
              keys.slice(0, 5).forEach((k) => {
                tableArray += "<tr><td>" + (k.replace("e1_6_1-", "") || "").replace(/</g, "&lt;") + '</td><td colspan="10">' + (ta[k] != null ? String(ta[k]).replace(/</g, "&lt;") : "") + "</td></tr>";
              });
            }
          } catch (_) {}
        } else {
          rows.forEach((child) => {
            if (!child?.props?.children) return;
            tableArray += "<tr>";
            const firstChild = child.props.children;
            const isArray = Array.isArray(firstChild);
            const singleEl = isArray ? null : firstChild;
            const singleProps = singleEl?.props;
            const hasColSpan = singleProps?.colSpan != null;
            // Section header row: single <th colSpan="11">Scope 1 GHG emissions</th>
            if (!isArray && hasColSpan && singleProps) {
              const text = typeof singleProps.children === "string" ? singleProps.children : (singleProps.children?.props?.children ?? String(singleProps.children ?? ""));
              tableArray += '<td colspan="' + (singleProps.colSpan ?? 11) + '">' + (text || "").replace(/</g, "&lt;") + "</td>";
              tableArray += "</tr>";
              return;
            }
            const cells = isArray ? firstChild : firstChild != null ? [firstChild] : [];
            cells.forEach((cell) => {
              const inner = cell?.props?.children;
              const valueProp = inner?.props?.value;
              if (valueProp !== undefined && valueProp !== null) {
                tableArray += "<td>" + String(valueProp).replace(/</g, "&lt;") + "</td>";
              } else {
                const text = typeof inner === "string" ? inner : (inner?.props?.children ?? "");
                tableArray += "<td>" + (text != null ? String(text).replace(/</g, "&lt;") : "") + "</td>";
              }
            });
            tableArray += "</tr>";
          });
        }
        tableArray += "</tbody>";
        tableArray += "</table>";
        markQuestionSaved(question);
        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module || "Environment",
          id: id,
        };

        setStepData((prevData) => {
          if (!prevData) {
            localStorage.setItem("step4", JSON.stringify([newData]));
            return [newData];
          }
          const idx = id ? prevData.findIndex((item) => item.id === id) : -1;
          const updated = idx >= 0 ? [...prevData.slice(0, idx), newData, ...prevData.slice(idx + 1)] : [...prevData, newData];
          localStorage.setItem("step4", JSON.stringify(updated));
          return updated;
        });
      } else {
        // Answer invalid or missing: save fallback table from tableAnswers so PDF still has E1-6 content
        const tableArray = buildFallbackTable();
        markQuestionSaved(question);
        setStepData((prevData) => {
          const newData = { title, question, answer: tableArray, module: module || "Environment", id };
          if (!prevData) {
            localStorage.setItem("step4", JSON.stringify([newData]));
            return [newData];
          }
          const idx = id ? prevData.findIndex((item) => item.id === id) : -1;
          const updated = idx >= 0 ? [...prevData.slice(0, idx), newData, ...prevData.slice(idx + 1)] : [...prevData, newData];
          localStorage.setItem("step4", JSON.stringify(updated));
          return updated;
        });
      }
    }
    if ("2ColumnWH" === fun) {
      if (answer && answer.props && answer.props.children) {
        const childrenArray = answer.props.children[1].props.children;
        let tableArray = "<table>";
        tableArray += "<tbody>";
        childrenArray.forEach((child) => {
          tableArray += "<tr>";
          child.props.children.forEach((child1) => {
            const ifArray = child1.props.children.props;
            const elseArray = child1.props.children;

            if (ifArray !== undefined) {
              tableArray += "<td>" + ifArray.value + "</td>";
            } else {
              tableArray += "<th>" + elseArray + "</th>";
            }
          });
          tableArray += "</tr>";
        });

        tableArray += "</tbody>";
        tableArray += "</table>";
        markQuestionSaved(question);
        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
    if ("TableWaste" === fun) {
      if (answer && answer.props && answer.props.children) {
        // Find the table element, accounting for wrapper divs
        let tableElement = answer;
        if (answer.type === "div" && answer.props.children) {
          const children = Array.isArray(answer.props.children) ? answer.props.children : [answer.props.children];
          tableElement = children.find((child) => child?.type === "table") || answer;
        }

        // Extract tbody rows from table's children
        let childrenArray = [];
        if (tableElement && tableElement.props && tableElement.props.children) {
          const tableChildren = Array.isArray(tableElement.props.children) ? tableElement.props.children : [tableElement.props.children];
          const tbody = tableChildren.find((child) => child?.type === "tbody");
          if (tbody && tbody.props && tbody.props.children) {
            const rows = Array.isArray(tbody.props.children) ? tbody.props.children : [tbody.props.children];
            childrenArray = rows.filter((r) => r && r.type === "tr");
          }
        }

        let tableArray = "<table>";

        tableArray +=
          '<thead><tr><th rowspan="2">Waste Category</th><th rowspan="2">Weight of total Waste Generated</th><th colspan="3">Weight of waste diverted from disposal</th><th colspan="3">Weight of waste diverted to disposal</th><th colspan="2">Non recycled waste</th></tr><tr><th>For reuse</th><th>For recycling</th><th>Other recovery</th><th>To incineration</th><th>To landfill</th><th>To other disposal operations</th><th>Weight of non-recycled waste</th><th>% of non-recycled waste</th></tr></thead>';

        tableArray += "<tbody>";
        childrenArray.forEach((child) => {
          if (!child) return;
          tableArray += "<tr>";
          const cells = Array.isArray(child.props?.children) ? child.props.children : child.props?.children ? [child.props.children] : [];
          cells.forEach((child1) => {
            if (!child1) return;
            const ifArray = child1.props?.children?.props;
            const elseArray = child1.props?.children;

            if (ifArray !== undefined) {
              tableArray += "<td>" + (ifArray.value != null ? ifArray.value : "") + "</td>";
            } else {
              tableArray += "<th>" + (elseArray != null ? elseArray : "") + "</th>";
            }
          });
          tableArray += "</tr>";
        });

        tableArray += "</tbody>";
        tableArray += "</table>";

        markQuestionSaved(question);
        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
    if ("S114" === fun) {
      // S1-14 Health and safety metrics table with headers in first column
      if (answer && answer.props && answer.props.children) {
        const rawChildrenArray = answer.props.children[1]?.props?.children;
        const childrenArray = Array.isArray(rawChildrenArray) ? rawChildrenArray : rawChildrenArray ? [rawChildrenArray] : [];
        let tableArray = "<table>";

        tableArray += "<thead>";
        tableArray += "<tr>";
        tableArray += "<th>Health and safety metrics</th>";
        tableArray += "<th>Employees</th>";
        tableArray += "<th>Non-employees</th>";
        tableArray += "<th>Site employees</th>";
        tableArray += "<th>Value chain employees</th>";
        tableArray += "</tr>";
        tableArray += "</thead>";

        tableArray += "<tbody>";
        childrenArray.forEach((child) => {
          if (!child) return;
          tableArray += "<tr>";
          const cells = Array.isArray(child.props?.children) ? child.props.children : child.props?.children ? [child.props.children] : [];
          cells.forEach((child1) => {
            if (!child1) return;
            const ifArray = child1.props?.children?.props;
            const elseArray = child1.props?.children;

            if (ifArray !== undefined) {
              tableArray += "<td>" + (ifArray.value != null ? ifArray.value : "") + "</td>";
            } else {
              tableArray += "<th>" + (elseArray != null ? elseArray : "") + "</th>";
            }
          });
          tableArray += "</tr>";
        });

        tableArray += "</tbody>";
        tableArray += "</table>";
        markQuestionSaved(question);
        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
    if ("6Column" === fun) {
      if (answer && answer.props && answer.props.children) {
        const childrenArray = answer.props.children[1].props.children;
        let tableArray = "<table>";

        tableArray += "<thead>";
        tableArray += "<tr>";
        tableArray += '<th colspan="6">S1-7 – Characteristics of non-employee workers in the undertaking’s own workforce</th>';
        tableArray += "</tr>";
        tableArray += "<tr>";
        tableArray += "<th>Informations on employess by gender</th>";
        tableArray += "<th>Male</th>";
        tableArray += "<th>Female</th>";
        tableArray += "<th>other</th>";
        tableArray += "<th>Not disclosed</th>";
        tableArray += "<th>Total</th>";
        tableArray += "</tr>";
        tableArray += "</thead>";

        tableArray += "<tbody>";
        childrenArray.forEach((child) => {
          tableArray += "<tr>";
          child.props.children.forEach((child1) => {
            const ifArray = child1.props.children.props;
            const elseArray = child1.props.children;

            if (ifArray !== undefined) {
              tableArray += "<td>" + ifArray.value + "</td>";
            } else {
              tableArray += "<th>" + elseArray + "</th>";
            }
          });
          tableArray += "</tr>";
        });

        tableArray += "</tbody>";
        tableArray += "</table>";
        markQuestionSaved(question);
        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
    if ("6Column1" === fun) {
      const tableAnswers = JSON.parse(localStorage.getItem("tableAnswers") || "{}");
      const storedHeaders = loadS1_6ColumnHeaders();
      const tableArray = buildS1_6TableHtmlFromAnswers(tableAnswers, storedHeaders);
      markQuestionSaved(question);
      upsertStepData({
        title,
        question,
        answer: tableArray,
        module,
        id,
      });
      return;
    }
    if ("E54B" === fun || "E54S" === fun || "E54R" === fun) {
      if (answer && answer.props && answer.props.children) {
        const childrenArray = answer.props.children[1].props.children;
        let tableArray = "<table>";
        if (fun === "E54B") {
          tableArray += '<thead><tr><th rowspan="2" colspan="2">Resource and material inflows</th><th colspan="3" style={{ textAlign: "center" }}>Weight of Material</th></tr><tr><th>Biological</th><th>Non-bio</th><th>% Biological</th></tr>';
        }
        if (fun === "E54S") {
          tableArray += '<thead><tr><th rowspan="2" colspan="2">Resource and material inflows</th><th colspan="3" style={{ textAlign: "center" }}>Weight of Material</th></tr><tr><th>Sustainably Sourced</th><th>Non-sustainably sourced</th><th>% Sustainably sourced</th></tr>';
        }
        if (fun === "E54R") {
          tableArray += '<thead><tr><th rowspan="2" colspan="2">Resource and material inflows</th><th colspan="3" style={{ textAlign: "center" }}>Weight of Material</th></tr><tr><th>Recycled/reused</th><th>Virgin</th><th>% Recycled/reused</th></tr>';
        }

        tableArray += "<tbody>";
        childrenArray.forEach((child) => {
          tableArray += "<tr>";
          child.props.children.forEach((child1) => {
            const ifArray = child1.props.children;
            const otherArray = child1.props;
            let hasData = false;
            // Check if ifArray has children props with data
            if (ifArray !== undefined) {
              hasData = true;
              if (ifArray.props && ifArray.props.value !== undefined) {
                tableArray += "<td>" + ifArray.props.value + "</td>";
              } else {
                if (otherArray.rowSpan && otherArray.rowSpan !== undefined) {
                  tableArray += "<th rowSpan=" + otherArray.rowSpan + ">" + otherArray.children + "</thzzzcxdg>";
                } else if (otherArray.colSpan && otherArray.colSpan !== undefined) {
                  tableArray += "<th colSpan=" + otherArray.colSpan + ">" + otherArray.children + "</th>";
                } else {
                  tableArray += "<th>" + ifArray + "</th>";
                }
              }
            } else {
            }
          });
          tableArray += "</tr>";
        });

        tableArray += "</tbody>";
        tableArray += "</table>";
        markQuestionSaved(question);
        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
    if ("S1-12" === fun) {
      if (answer && answer.props && answer.props.children) {
        const headerArray = answer.props.children[0].props.children.props.children;
        const childrenArray = answer.props.children[1].props.children.props.children;
        let tableArray = "<table>";

        tableArray += "<thead><tr>";
        tableArray += "<th>Employment of persons with disabilitie</th>";
        tableArray += "<th>Male</th>";
        tableArray += "<th>Female</th>";
        tableArray += "</tr></thead>";
        tableArray += "<tbody>";
        tableArray += "<tr>";
        childrenArray.forEach((child) => {
          const ifArray = child.props.children.props;
          const elseArray = child.props.children;

          if (ifArray !== undefined) {
            tableArray += "<td>" + ifArray.value + "</td>";
          } else {
            tableArray += "<th>" + elseArray + "</th>";
          }
        });
        tableArray += "</tr>";

        tableArray += "</tbody>";
        tableArray += "</table>";
        markQuestionSaved(question);
        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
    if ("3Column97" === fun) {
      if (answer && answer.props && answer.props.children) {
        const headerArray = answer.props.children[0].props.children.props.children;
        const childrenArray = answer.props.children[1].props.children.props.children;
        let tableArray = "<table>";

        tableArray += "<thead>";
        tableArray += "<tr>";
        headerArray.forEach((headerRow) => {
          if (headerRow.props.children === undefined) {
            tableArray += "<th></th>";
          } else {
            tableArray += "<th>" + headerRow.props.children + "</th>";
          }
        });
        tableArray += "</tr>";
        tableArray += "</thead>";

        tableArray += "<tbody>";

        tableArray += "<tr>";
        childrenArray.forEach((child1) => {
          const ifArray = child1.props.children.props;
          const elseArray = child1.props.children;

          if (ifArray !== undefined) {
            tableArray += "<td>" + ifArray.value + "</td>";
          } else {
            tableArray += "<th>" + elseArray + "</th>";
          }
        });
        tableArray += "</tr>";

        tableArray += "</tbody>";
        tableArray += "</table>";
        markQuestionSaved(question);
        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
    if ("E1-7" === fun) {
      if (answer && answer.props && answer.props.children) {
        const headerArray = answer.props.children.props.children[0].props.children.props.children;
        const childrenArray = answer.props.children.props.children[1].props.children;

        let tableArray = "<table>";

        tableArray += "<thead>";
        tableArray += "<tr>";
        headerArray.forEach((headerRow) => {
          if (headerRow.props.children === undefined) {
            tableArray += "<th></th>";
          } else {
            tableArray += "<th>" + headerRow.props.children + "</th>";
          }
        });
        tableArray += "</tr>";
        tableArray += "</thead>";

        tableArray += "<tbody>";
        let loopCount = 0;
        childrenArray.forEach((child) => {
          loopCount++;
          tableArray += "<tr>";
          // Initialize a counter variable
          if (loopCount <= 9) {
            child.props.children.forEach((child1) => {
              const ifArray = child1.props.children.props;
              const elseArray = child1.props.children;

              if (ifArray !== undefined) {
                tableArray += "<td>" + ifArray.value + "</td>";
              } else {
                tableArray += "<th>" + elseArray + "</th>";
              }
            });
          } else {
            child.props.children.forEach((child1) => {
              const ifArray = child1.props.children.props;
              const elseArray = child1.props.children;
              const elseArraycolSpan = child1.props.colSpan;

              if (ifArray !== undefined) {
                tableArray += '<td colSpan="' + elseArraycolSpan + '">' + ifArray.value + "</td>";
              } else {
                if (elseArraycolSpan !== undefined) {
                  tableArray += '<th colSpan="' + elseArraycolSpan + '">' + elseArray + "</th>";
                } else {
                  tableArray += "<th>" + elseArray + "</th>";
                }
              }
            });
          }
          tableArray += "</tr>";
        });

        tableArray += "</tbody>";
        tableArray += "</table>";
        markQuestionSaved(question);
        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
    if ("GHGIntensity" === fun) {
      if (answer && answer.props && answer.props.children) {
        // Unwrap: table may be wrapped in div (table-scroll-wrapper-custom); single child may be element not array
        const isDivWrapper = answer.props?.className === "table-scroll-wrapper-custom";
        const tableEl = isDivWrapper ? (Array.isArray(answer.props.children) ? answer.props.children[0] : answer.props.children) : answer;
        const thead = tableEl?.props?.children?.[0];
        const tbody = tableEl?.props?.children?.[1];
        const headerArray = thead?.props?.children?.props?.children ?? [];
        const childrenArray = tbody?.props?.children ?? [];
        if (!headerArray.length && !childrenArray.length) return;

        let tableArray = "<table>";

        tableArray += "<thead>";
        tableArray += "<tr>";
        headerArray.forEach((headerRow) => {
          if (headerRow.props.children === undefined) {
            tableArray += "<th></th>";
          } else {
            tableArray += "<th>" + headerRow.props.children + "</th>";
          }
        });
        tableArray += "</tr>";
        tableArray += "</thead>";

        tableArray += "<tbody>";
        let loopCount = 0;
        childrenArray.forEach((child) => {
          loopCount++;
          //console.log(child);
          if (loopCount <= 2) {
            tableArray += "<tr>";
            (child?.props?.children || []).forEach((row) => {
              const ifArray = row?.props?.children?.props;
              const elseArray = row?.props?.children;
              if (ifArray !== undefined) {
                tableArray += "<td>" + (ifArray.value != null ? ifArray.value : "") + "</td>";
              } else {
                tableArray += "<td>" + (elseArray != null ? elseArray : "") + "</td>";
              }
            });
            tableArray += "</tr>";
          } else {
            tableArray += "<tr>";
            (child?.props?.children || []).forEach((row, index) => {
              if (index < 3) {
                if (row?.props?.colSpan) {
                  const cellValue = row?.props?.children?.props?.value ?? row?.props?.children ?? "";
                  tableArray += "<td colspan='" + row.props.colSpan + "'>" + (typeof cellValue === "string" ? cellValue : cellValue != null ? cellValue : "") + "</td>";
                } else {
                  const cellValue = row?.props?.children?.props?.value ?? row?.props?.children ?? "";
                  tableArray += "<td>" + (typeof cellValue === "string" ? cellValue : cellValue != null ? cellValue : "") + "</td>";
                }
              }
            });
            tableArray += "</tr>";
          }
        });

        tableArray += "</tbody>";
        tableArray += "</table>";
        markQuestionSaved(question);
        const newData = {
          title: title,
          question: question,
          answer: tableArray,
          module: module,
          id: id,
        };

        upsertStepData(newData);
      }
    }
  };

  const handleSave = (title, question, answer, module, id) => {
    if (!answer && answer !== 0) {
      return false;
    }
    setAnswers((prevAnswers) => {
      const updatedAnswers = {
        ...prevAnswers,
        [id]: answer,
      };
      // Save updated answers to localStorage
      localStorage.setItem("answers", JSON.stringify(updatedAnswers));
      localStorage.setItem("answers1", JSON.stringify(updatedAnswers));
      return updatedAnswers;
    });
    // Implement logic to save the answers
    markQuestionSaved(question);
    const newData = {
      title: title,
      question: question,
      answer: answer,
      module: module,
      id: id,
    };

    upsertStepData(newData);
    return true;
  };

  const handleUseGeneratedText = (generatedText) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [selectedQuestion]: generatedText,
    }));
    closeModal();
  };

  const handleSubmit = () => {
    // Implement logic to submit the answers
    //console.log("Answers submitted:", answers);
  };

  const filteredQuestions = questions.filter(
    (questionObj) =>
      !questionObj.condition ||
      questionObj.condition === selectedOptions[selectedSubSubItem?.id]
  );

  // When any CSRD table cell changes (handled in getQuestionData.js), we receive a
  // 'csrd-table-changed' event on window. Ideally, only the Save icon for the
  // specific table that changed should turn grey. We introspect each table's
  // React element and look for inputs whose name shares the same key prefix
  // (e.g. `e1_6_1-` vs `e1_6_2-`) to determine which table was edited.
  useEffect(() => {
    const tableQuestions = filteredQuestions.filter((q) => q.table);
    if (tableQuestions.length === 0) return;

    const elementHasPrefix = (element, prefix) => {
      if (!element || !prefix) return false;
      const props = element.props || {};

      const name = props.name;
      if (typeof name === "string" && (name.startsWith(prefix + "-") || name.startsWith(prefix + "_"))) {
        return true;
      }

      const children = props.children;
      if (!children) return false;

      const arr = Array.isArray(children) ? children : [children];
      for (const child of arr) {
        if (child && elementHasPrefix(child, prefix)) {
          return true;
        }
      }
      return false;
    };

    const handleTableChanged = (event) => {
      const detail = event?.detail || {};
      // Prefer explicit table identifier from the event; fall back to name prefix.
      const explicitTableKey = detail.table;
      const namePrefix =
        typeof detail.name === "string" && detail.name.includes("-")
          ? detail.name.split("-")[0]
          : undefined;
      const prefix = explicitTableKey || namePrefix;

      // Find only the table questions whose JSX contains inputs with this prefix.
      const impacted = prefix
        ? tableQuestions.filter((q) => elementHasPrefix(q.table, prefix))
        : tableQuestions;

      if (impacted.length === 0) {
        return;
      }

      setSavedStatus((prev) => {
        const next = { ...prev };
        impacted.forEach((q) => {
          const key = (q.question || "").trim();
          if (key in next) {
            next[key] = false;
          }
        });
        localStorage.setItem("savedStatus", JSON.stringify(next));
        return next;
      });
    };

    window.addEventListener("csrd-table-changed", handleTableChanged);
    return () => {
      window.removeEventListener("csrd-table-changed", handleTableChanged);
    };
  }, [filteredQuestions]);

  useImperativeHandle(
    ref,
    () => ({
      saveCurrentSectionTables() {
        filteredQuestions
          .filter((q) => q.table)
          .forEach((q) => handleTableSave(selectedContent, q.question, q.table, q.module, q.fun, q.id));
      },
      triggerGenerateAI() {
        fetchData(selectedContent);
      },
    }),
    [filteredQuestions, selectedContent, fetchData],
  );

  return (
    <div className="right">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      <AIPopup aiModalOpen={aiModalOpen} onClose={closeModal} question={selectedQuestion} answer={selectedAnswer} onUseText={handleUseGeneratedText} />
      <div className="questions-header">
        <div className="topic-title">
          <Typography variant="h6">{selectedContent}</Typography>

          <div className="checkbox-iconbox">
            {selectedSubSubItem?.mandatory ? ( // Check if mandatory is true
              <img className="accordian-icon" src="./assets/img/reports/Mandatory.svg" alt="Mandatory icon" />
            ) : null}
            {selectedSubSubItem?.tooltip && ( // Check if selectedSubSubItem exists and has a tooltip
              <Tooltip title={selectedSubSubItem.tooltip}>
                <IconButton style={{ height: "0", width: "0", marginLeft: "8px" }}>
                  <img className="accordian-icon" src="./assets/img/reports/info.svg" alt="Info Icon" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
        {selectedSubSubItem?.obj && ( // Check if obj is present
          <div className="objective-box">
            {typeof selectedSubSubItem.obj === "string" ? ( // Check if obj is a string
              <Typography
                className="objective"
                variant="body1"
                style={{ whiteSpace: "pre-line" }} // Preserve line breaks
              >
                {isExpanded ? selectedSubSubItem.obj : selectedSubSubItem.obj.length > 75 ? selectedSubSubItem.obj.slice(0, 75) + "..." : selectedSubSubItem.obj}
                {selectedSubSubItem.obj.length > 75 && ( // Check if objective length exceeds 100 characters
                  <span onClick={toggleExpand}>{isExpanded ? " Read Less" : " Read More"}</span>
                )}
              </Typography>
            ) : (
              // If obj is not a string, assume it's a JSX element
              <div>
                {selectedSubSubItem.obj}
                {/* Check if JSX element has more than 100 characters */}
                {selectedSubSubItem.obj.props.children.length > 100 && <span onClick={toggleExpand}>{isExpanded ? "Read Less" : "Read More"}</span>}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="mdr-container">
        {selectedSubSubItem && selectedSubSubItem.mdr && (
          <div className="mdr-content d-flex align-items-center justify-content-between">
            <p className="mb-0">{selectedSubSubItem.mdr}</p>
            <div className="flex flex-row" style={{ gap: "20px" }}>
              <div className="flex align-items-center">
                <RadioButton inputId={`${selectedSubSubItem.id}-yes`} name={`${selectedSubSubItem.id}-mdrOption`} value="yes" onChange={(e) => handleOptionChange(selectedSubSubItem.id, e.value)} checked={selectedOptions[selectedSubSubItem.id] === "yes"} />
                <label htmlFor={`${selectedSubSubItem.id}-yes`} className="ml-2 mb-0">
                  Yes
                </label>
              </div>
              <div className="flex align-items-center">
                <RadioButton inputId={`${selectedSubSubItem.id}-no`} name={`${selectedSubSubItem.id}-mdrOption`} value="no" onChange={(e) => handleOptionChange(selectedSubSubItem.id, e.value)} checked={selectedOptions[selectedSubSubItem.id] === "no"} />
                <label htmlFor={`${selectedSubSubItem.id}-no`} className="ml-2 mb-0">
                  No
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="questions-container">
        {questions && (
          <div className="input-questions-box">
            {filteredQuestions.map((questionObj, index) =>
              questionObj.table ? (
                <div className="table-container" key={index}>
                  <div className="table-heading">
                    <div className="question-text-wrap">
                      <Typography variant="body1">
                        <span className="bold-part">{questionObj.boldPart}</span>
                        {questionObj.question}
                      </Typography>
                    </div>
                    <div className="table-icons">
                      {questionObj.tooltip && ( // Conditionally render Tooltip
                        <Tooltip title={questionObj.tooltip}>
                          <IconButton sx={{ padding: "0" }} className="input-info">
                            <img className="accordian-icon" src="./assets/img/reports/info.svg" alt="Info Icon" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {questionObj.voluntary && ( // Conditionally render Voluntary icon
                        <Tooltip title="Voluntary">
                          <IconButton sx={{ padding: "0" }} className="input-info">
                            <img className="accordian-icon" src="./assets/img/reports/voluntary.svg" alt="Voluntary Icon" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {questionObj.phasedIn && ( // Conditionally render Tooltip
                        <Tooltip title={questionObj.phasedIn}>
                          <IconButton sx={{ padding: "0" }} className="input-info">
                            <img className="accordian-icon" src="./assets/img/reports/phased-in.svg" alt="Phased-in Icon" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {questionObj.euReg && ( // Conditionally render Tooltip
                        <Tooltip title={questionObj.euReg}>
                          <IconButton sx={{ padding: "0" }} className="input-info">
                            <img className="accordian-icon" src="./assets/img/reports/eu-regulation.png" alt="EU Regulation Icon" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  {questionObj.fun === "Scope123" || questionObj.fun === "GHGIntensity" ? questionObj.table : <div className="table-scroll-wrapper">{questionObj.table}</div>}
                  <div className="answer-icon">
                    <Tooltip title="Save your answer">
                      <IconButton
                        onClick={() => handleTableSave(selectedContent, questionObj.question, questionObj.table, questionObj.module, questionObj.fun, questionObj.id)}
                        size="small"
                        color="primary"
                        style={{
                          color: "#707070",
                          float: "right",
                          marginTop: "10px",
                        }}
                      >
                        {savedStatus[questionObj.question.trim()] ? <img className="accordian-icon" src="./assets/img/reports/tick-mark-green.png" alt="Answer Saved" /> : <img className="accordian-icon" src="./assets/img/reports/tick-mark-grey.png" alt="Save" />}
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              ) : (
                <div className="question-tab" key={index}>
                  <div className="question-box">
                    <div className="question-text-wrap">
                      <Typography variant="body1">
                        <span className="bold-part">{questionObj.boldPart}</span> {questionObj.question}
                      </Typography>
                    </div>
                    <div className="questions-icons">
                      {questionObj.tooltip && ( // Conditionally render Tooltip
                        <CustomWidthTooltip title={questionObj.tooltip}>
                          <IconButton sx={{ padding: "0", m: 0 }} className="input-info">
                            <img className="accordian-icon" src="./assets/img/reports/info.svg" alt="Info Icon" />
                          </IconButton>
                        </CustomWidthTooltip>
                      )}
                      {questionObj.voluntary && ( // Conditionally render Voluntary icon
                        <Tooltip title="Voluntary">
                          <IconButton sx={{ padding: "0" }} className="input-info">
                            <img className="accordian-icon" src="./assets/img/reports/voluntary.svg" alt="Voluntary Icon" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {questionObj.phasedIn && ( // Conditionally render Tooltip
                        <Tooltip title={questionObj.phasedIn}>
                          <IconButton sx={{ padding: "0" }} className="input-info">
                            <img className="accordian-icon" src="./assets/img/reports/phased-in.svg" alt="Phased-in Icon" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {questionObj.euReg && ( // Conditionally render Tooltip
                        <Tooltip title={questionObj.euReg}>
                          <IconButton sx={{ padding: "0", m: 0 }} className="input-info">
                            <img className="accordian-icon" src="./assets/img/reports/eu-regulation.png" alt="EU Regulation Icon" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <div className="answer-box">
                    <div style={{ position: "relative" }}>
                      <textarea
                        placeholder="Enter your answer"
                        value={answers1[questionObj.id] || ""}
                        id={questionObj.id}
                        onChange={(e) => handleAnswerChange(questionObj.question, e.target.value, questionObj.id)}
                      />
                      <div className="question-iconbox">
                        {/* <div
                          className={`ai-button ${!answers[questionObj.question] && "disabled"}`}
                          onClick={() =>
                            answers[questionObj.id] &&
                            openModal(questionObj.question, answers[questionObj.id])
                          }
                        >
                          <img
                            className="accordian-icon"
                            src="./assets/img/reports/ai.svg"
                            alt="AI Icon"
                          />
                          <span style={{ marginLeft: "5px" }}>Generate with AI</span>
                        </div> */}

                        <div className="save-button">
                          <Tooltip title="Save your answer">
                            <IconButton onClick={() => handleSave(selectedContent, questionObj.question, answers1[questionObj.id], questionObj.module, questionObj.id)} size="small" color="primary" style={{ color: "#707070" }}>
                              {answers[questionObj.id] ? <img className="accordian-icon" src="./assets/img/reports/tick-mark-green.png" alt="Answer Saved" /> : <img className="accordian-icon" src="./assets/img/reports/tick-mark-grey.png" alt="Save" />}
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default RightContainer;
